import type { TemplateQuery } from '@/services/api/dataApi';
import type {
  AggregationFunction,
  ChartGroupBy,
  ChartMeasure,
  ChartQueryDefinition,
  ChartTimeRange,
  CompositeFieldMapping,
  CompositeJoinKey,
  CompositeJoinType,
  CompositeQueryDefinition,
  ComputedField,
  TimeGranularity,
} from '@/types/chartDataSource';
import type { CreateResultMetadata, CreateTemplateResult } from './DataDiscoveryModal.createLogic';
import {
  buildGeneratedCreateSingleSourceQueryDefinition,
  getGeneratedCreateDisabledReasons,
  resolveGeneratedCreateSingleSourceOrganizationScope,
  type GeneratedCreateActionOptions,
} from './DataDiscoveryModal.generatedActions';

const MISSING_SOURCE_REASON = 'Choose a data source before saving or applying this manual draft.';
const MISSING_COMPOSITE_SOURCE_REASON = 'Choose at least two data sources before saving or applying this manual draft.';
const MISSING_MEASURE_REASON = 'Choose at least one measure before saving or applying this manual draft.';
const MISSING_JOIN_KEY_REASON = 'Choose at least one join key before saving or applying this manual draft.';
const MISSING_OUTPUT_FIELDS_REASON = 'Choose at least one output field before saving or applying this manual draft.';
const INCOMPLETE_COMPOSITE_JOIN_IMPORT_WARNING =
  'The generated multi-source query is missing join details. Review the Join step before saving.';
const PARTIAL_COMPOSITE_OUTPUT_REASON =
  'Choose output fields from at least two sources before saving or applying this manual draft.';
const INVALID_AGGREGATION_REASON = 'Choose a supported aggregation before saving or applying this manual draft.';
const INVALID_GROUP_BY_REASON = 'Choose a supported group-by granularity before saving or applying this manual draft.';
const INVALID_PRIMARY_SOURCE_REASON = 'Choose a primary source that exists in the manual draft.';
const INVALID_FIELD_SOURCE_REASON = 'Output fields must reference a source in the manual draft.';
const INVALID_JOIN_SOURCE_REASON = 'Join keys must include left and right fields.';
const UNRESOLVED_TEMPLATE_VARIABLE_REASON =
  'Resolve template variables before saving or applying this manual draft.';
const GENERATED_IMPORT_FALLBACK_REASON =
  'This generated result does not include a safe structured template to edit manually.';

const TEMPLATE_PLACEHOLDER_PATTERN = /{{\s*[^}]+\s*}}/;

type SupportedAggregationFunction = Exclude<AggregationFunction, 'none'>;

export interface ManualDraftSource {
  schemaId?: string;
  groupId?: string;
  schemaName?: string;
  sourceId?: string;
  sourceName?: string;
  organizationId?: string;
  organizationName?: string;
}

export interface ManualMeasureDraft {
  field?: string;
  aggregation?: string;
  alias?: string;
}

export interface ManualGroupByDraft {
  field?: string;
  granularity?: string;
}

export interface ManualTimeRangeDraft {
  field?: string;
  preset?: string;
  from?: string;
  to?: string;
}

export interface ManualOrderByDraft {
  field?: string;
  direction: 'asc' | 'desc';
}

interface ManualNormalizedOrderBy {
  field: string;
  direction: 'asc' | 'desc';
}

export interface ManualSingleSourceQueryDraft {
  kind: 'single-source';
  source?: ManualDraftSource | null;
  measures: ManualMeasureDraft[];
  timeRange?: ManualTimeRangeDraft;
  groupBy?: ManualGroupByDraft[];
  filters?: Record<string, unknown>;
  orderBy?: ManualOrderByDraft[];
  limit?: number;
  previewRows?: Record<string, unknown>[];
}

export interface ManualCompositeSourceDraft {
  id?: string;
  label?: string;
  source?: ManualDraftSource | null;
  measures: ManualMeasureDraft[];
  timeRange?: ManualTimeRangeDraft;
  groupBy?: ManualGroupByDraft[];
  filters?: Record<string, unknown>;
  orderBy?: ManualOrderByDraft[];
  limit?: number;
}

export interface ManualCompositeFieldDraft {
  sourceId?: string;
  field?: string;
  alias?: string;
}

export interface ManualComputedFieldDraft {
  name?: string;
  expression?: string;
}

export type ManualJoinKeyDraft =
  | {
      left?: string;
      right?: string;
      kind?: 'exact' | 'time-bucket';
      granularity?: string;
    }
  | {
      left?: string;
      right?: string;
      kind: 'nearest-timestamp';
      toleranceMs?: number;
      granularity?: string;
    };

export interface ManualCompositeQueryDraft {
  kind: 'composite';
  sources: ManualCompositeSourceDraft[];
  primarySourceId?: string;
  joinType: CompositeJoinType;
  joinKeys: ManualJoinKeyDraft[];
  fields: ManualCompositeFieldDraft[];
  computedFields?: ManualComputedFieldDraft[];
  orderBy?: ManualOrderByDraft[];
  limit?: number;
  previewRows?: Record<string, unknown>[];
}

export interface ManualPeriodComparisonDraft extends Omit<ManualCompositeQueryDraft, 'kind'> {
  kind: 'period-comparison';
}

export type ManualCreateDraft =
  | ManualSingleSourceQueryDraft
  | ManualCompositeQueryDraft
  | ManualPeriodComparisonDraft;

export type ManualDraftValidationResult =
  | {
      ok: true;
    }
  | {
      ok: false;
      disabledReason: string;
    };

export type ManualCreateTemplateResult =
  | {
      ok: true;
      result: CreateTemplateResult;
    }
  | {
      ok: false;
      disabledReason: string;
    };

export type GeneratedManualDraftImportResult =
  | {
      ok: true;
      draft: ManualCreateDraft;
      warning?: string;
    }
  | {
      ok: false;
      disabledReason: string;
    };

export interface ManualCreateActionState {
  isActionable: boolean;
  canSave: boolean;
  canApply: boolean;
  save: string | null;
  apply: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function normalizeOptionalString(value: string | undefined): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function isSupportedAggregation(value: string | undefined): value is SupportedAggregationFunction {
  switch (value) {
    case 'sum':
    case 'avg':
    case 'min':
    case 'max':
    case 'count':
    case 'last':
    case 'first':
      return true;
    default:
      return false;
  }
}

function isTimeGranularity(value: string | undefined): value is TimeGranularity {
  switch (value) {
    case 'minute':
    case 'hour':
    case 'day':
    case 'week':
    case 'month':
      return true;
    default:
      return false;
  }
}

function hasTemplatePlaceholder(value: string): boolean {
  return TEMPLATE_PLACEHOLDER_PATTERN.test(value);
}

function hasUnresolvedTemplatePlaceholder(value: unknown): boolean {
  if (typeof value === 'string') {
    return hasTemplatePlaceholder(value);
  }

  if (Array.isArray(value)) {
    return value.some((entry) => hasUnresolvedTemplatePlaceholder(entry));
  }

  if (value !== null && typeof value === 'object') {
    return Object.values(value).some((entry) => hasUnresolvedTemplatePlaceholder(entry));
  }

  return false;
}

function validateSource(source: ManualDraftSource | null | undefined): ManualDraftValidationResult {
  if (!source || !normalizeOptionalString(source.schemaId) || !normalizeOptionalString(source.groupId)) {
    return { ok: false, disabledReason: MISSING_SOURCE_REASON };
  }

  return { ok: true };
}

function validateMeasures(measures: ManualMeasureDraft[]): ManualDraftValidationResult {
  if (measures.length === 0 || measures.every((measure) => !normalizeOptionalString(measure.field))) {
    return { ok: false, disabledReason: MISSING_MEASURE_REASON };
  }

  for (const measure of measures) {
    if (!normalizeOptionalString(measure.field)) {
      return { ok: false, disabledReason: MISSING_MEASURE_REASON };
    }

    if (!isSupportedAggregation(measure.aggregation)) {
      return { ok: false, disabledReason: INVALID_AGGREGATION_REASON };
    }
  }

  return { ok: true };
}

function validateGroupBy(groupBy: ManualGroupByDraft[] | undefined): ManualDraftValidationResult {
  for (const group of groupBy ?? []) {
    if (!normalizeOptionalString(group.field)) {
      return { ok: false, disabledReason: MISSING_OUTPUT_FIELDS_REASON };
    }

    if (group.granularity !== undefined && !isTimeGranularity(group.granularity)) {
      return { ok: false, disabledReason: INVALID_GROUP_BY_REASON };
    }
  }

  return { ok: true };
}

function validateOrderBy(orderBy: ManualOrderByDraft[] | undefined): ManualDraftValidationResult {
  for (const order of orderBy ?? []) {
    if (!normalizeOptionalString(order.field)) {
      return { ok: false, disabledReason: MISSING_OUTPUT_FIELDS_REASON };
    }
  }

  return { ok: true };
}

function validateSingleSourceDraft(draft: ManualSingleSourceQueryDraft): ManualDraftValidationResult {
  const sourceValidation = validateSource(draft.source);
  if (!sourceValidation.ok) {
    return sourceValidation;
  }

  const measureValidation = validateMeasures(draft.measures);
  if (!measureValidation.ok) {
    return measureValidation;
  }

  const groupByValidation = validateGroupBy(draft.groupBy);
  if (!groupByValidation.ok) {
    return groupByValidation;
  }

  const orderByValidation = validateOrderBy(draft.orderBy);
  if (!orderByValidation.ok) {
    return orderByValidation;
  }

  return hasUnresolvedTemplatePlaceholder(draft)
    ? { ok: false, disabledReason: UNRESOLVED_TEMPLATE_VARIABLE_REASON }
    : { ok: true };
}

function validateCompositeDraft(
  draft: ManualCompositeQueryDraft | ManualPeriodComparisonDraft,
): ManualDraftValidationResult {
  if (draft.sources.length < 2) {
    return { ok: false, disabledReason: MISSING_COMPOSITE_SOURCE_REASON };
  }

  const sourceIds = new Set<string>();
  for (const source of draft.sources) {
    const sourceValidation = validateSource(source.source);
    if (!sourceValidation.ok) {
      return sourceValidation;
    }

    const id = normalizeOptionalString(source.id);
    if (!id) {
      return { ok: false, disabledReason: MISSING_SOURCE_REASON };
    }

    sourceIds.add(id);

    const measureValidation = validateMeasures(source.measures);
    if (!measureValidation.ok) {
      return measureValidation;
    }

    const groupByValidation = validateGroupBy(source.groupBy);
    if (!groupByValidation.ok) {
      return groupByValidation;
    }

    const orderByValidation = validateOrderBy(source.orderBy);
    if (!orderByValidation.ok) {
      return orderByValidation;
    }
  }

  const primarySourceId = normalizeOptionalString(draft.primarySourceId);
  if (!primarySourceId || !sourceIds.has(primarySourceId)) {
    return { ok: false, disabledReason: INVALID_PRIMARY_SOURCE_REASON };
  }

  if (draft.joinKeys.length === 0) {
    return { ok: false, disabledReason: MISSING_JOIN_KEY_REASON };
  }

  for (const joinKey of draft.joinKeys) {
    if (!normalizeOptionalString(joinKey.left) || !normalizeOptionalString(joinKey.right)) {
      return { ok: false, disabledReason: INVALID_JOIN_SOURCE_REASON };
    }

    if (joinKey.kind === 'time-bucket' && !isTimeGranularity(joinKey.granularity)) {
      return { ok: false, disabledReason: MISSING_JOIN_KEY_REASON };
    }

    if (joinKey.kind === 'nearest-timestamp' && typeof joinKey.toleranceMs !== 'number') {
      return { ok: false, disabledReason: MISSING_JOIN_KEY_REASON };
    }
  }

  if (draft.fields.length === 0) {
    return { ok: false, disabledReason: MISSING_OUTPUT_FIELDS_REASON };
  }

  for (const field of draft.fields) {
    const sourceId = normalizeOptionalString(field.sourceId);
    if (!sourceId || !sourceIds.has(sourceId)) {
      return { ok: false, disabledReason: INVALID_FIELD_SOURCE_REASON };
    }

    if (!normalizeOptionalString(field.field) || !normalizeOptionalString(field.alias)) {
      return { ok: false, disabledReason: MISSING_OUTPUT_FIELDS_REASON };
    }
  }

  const projectedSourceIds = new Set(
    draft.fields.flatMap((field) => {
      const sourceId = normalizeOptionalString(field.sourceId);
      return sourceId && sourceIds.has(sourceId) ? [sourceId] : [];
    }),
  );
  if (projectedSourceIds.size < 2) {
    return { ok: false, disabledReason: PARTIAL_COMPOSITE_OUTPUT_REASON };
  }

  const orderByValidation = validateOrderBy(draft.orderBy);
  if (!orderByValidation.ok) {
    return orderByValidation;
  }

  return hasUnresolvedTemplatePlaceholder(draft)
    ? { ok: false, disabledReason: UNRESOLVED_TEMPLATE_VARIABLE_REASON }
    : { ok: true };
}

export function validateManualCreateDraft(draft: ManualCreateDraft): ManualDraftValidationResult {
  return draft.kind === 'single-source'
    ? validateSingleSourceDraft(draft)
    : validateCompositeDraft(draft);
}

function buildChartMeasures(measures: ManualMeasureDraft[]): ChartMeasure[] {
  return measures.flatMap((measure) => {
    const field = normalizeOptionalString(measure.field);
    if (!field || !isSupportedAggregation(measure.aggregation)) {
      return [];
    }

    return [
      {
        field,
        aggregation: measure.aggregation,
        ...(normalizeOptionalString(measure.alias) ? { alias: normalizeOptionalString(measure.alias) } : {}),
      },
    ];
  });
}

function buildGroupBy(groupBy: ManualGroupByDraft[] | undefined): ChartGroupBy[] | undefined {
  const normalized = (groupBy ?? []).flatMap((group) => {
    const field = normalizeOptionalString(group.field);
    if (!field) {
      return [];
    }

    return [
      {
        field,
        ...(isTimeGranularity(group.granularity) ? { granularity: group.granularity } : {}),
      },
    ];
  });

  return normalized.length > 0 ? normalized : undefined;
}

function buildTimeRange(timeRange: ManualTimeRangeDraft | undefined): ChartTimeRange | undefined {
  const field = normalizeOptionalString(timeRange?.field);
  if (!field) {
    return undefined;
  }

  return {
    field,
    ...(normalizeOptionalString(timeRange?.preset) ? { preset: normalizeOptionalString(timeRange?.preset) } : {}),
    ...(normalizeOptionalString(timeRange?.from) ? { from: normalizeOptionalString(timeRange?.from) } : {}),
    ...(normalizeOptionalString(timeRange?.to) ? { to: normalizeOptionalString(timeRange?.to) } : {}),
  };
}

function buildOrderBy(orderBy: ManualOrderByDraft[] | undefined): ManualNormalizedOrderBy[] | undefined {
  const normalized = (orderBy ?? []).flatMap((order) => {
    const field = normalizeOptionalString(order.field);
    return field ? [{ field, direction: order.direction }] : [];
  });

  return normalized.length > 0 ? normalized : undefined;
}

function importOrderBy(orderBy: ManualNormalizedOrderBy[] | undefined): ManualOrderByDraft[] | undefined {
  return orderBy?.map((order) => ({
    field: order.field,
    direction: order.direction,
  }));
}

function importGroupBy(groupBy: ChartGroupBy[] | undefined): ManualGroupByDraft[] | undefined {
  return groupBy?.map((group) => ({
    field: group.field,
    ...(group.granularity ? { granularity: group.granularity } : {}),
  }));
}

function importTimeRange(timeRange: ChartTimeRange | undefined): ManualTimeRangeDraft | undefined {
  return timeRange
    ? {
        field: timeRange.field,
        ...(timeRange.preset ? { preset: timeRange.preset } : {}),
        ...(timeRange.from ? { from: timeRange.from } : {}),
        ...(timeRange.to ? { to: timeRange.to } : {}),
      }
    : undefined;
}

function importMeasures(measures: ChartMeasure[]): ManualMeasureDraft[] {
  return measures.map((measure) => ({
    field: measure.field,
    aggregation: measure.aggregation,
    ...(measure.alias ? { alias: measure.alias } : {}),
  }));
}

function importPreviewRows(rows: unknown[] | undefined): Record<string, unknown>[] | undefined {
  const previewRows = (rows ?? []).flatMap((row) => (isRecord(row) ? [row] : []));
  return previewRows.length > 0 ? previewRows : undefined;
}

function buildChartQueryDefinition(
  source: ManualDraftSource,
  measures: ManualMeasureDraft[],
  input: {
    timeRange?: ManualTimeRangeDraft;
    groupBy?: ManualGroupByDraft[];
    filters?: Record<string, unknown>;
    orderBy?: ManualOrderByDraft[];
    limit?: number;
  },
): ChartQueryDefinition | null {
  const schemaId = normalizeOptionalString(source.schemaId);
  const groupId = normalizeOptionalString(source.groupId);
  const timeRange = buildTimeRange(input.timeRange);
  const groupBy = buildGroupBy(input.groupBy);
  const orderBy = buildOrderBy(input.orderBy);
  if (!schemaId || !groupId) {
    return null;
  }

  return {
    schemaId,
    groupId,
    schemaName: normalizeOptionalString(source.schemaName) ?? normalizeOptionalString(source.sourceName) ?? schemaId,
    measures: buildChartMeasures(measures),
    ...(normalizeOptionalString(source.organizationId) ? { organizationId: normalizeOptionalString(source.organizationId) } : {}),
    ...(normalizeOptionalString(source.organizationName) ? { organizationName: normalizeOptionalString(source.organizationName) } : {}),
    ...(timeRange ? { timeRange } : {}),
    ...(groupBy ? { groupBy } : {}),
    ...(input.filters ? { filters: input.filters } : {}),
    ...(orderBy ? { orderBy } : {}),
    ...(typeof input.limit === 'number' ? { limit: input.limit } : {}),
  };
}

function importChartQuerySource(
  queryDefinition: ChartQueryDefinition,
  input: {
    sourceId?: string;
    sourceName?: string;
  } = {},
): ManualDraftSource {
  return {
    schemaId: queryDefinition.schemaId,
    groupId: queryDefinition.groupId,
    schemaName: queryDefinition.schemaName,
    sourceId: input.sourceId,
    sourceName: input.sourceName ?? queryDefinition.schemaName,
    organizationId: queryDefinition.organizationId,
    organizationName: queryDefinition.organizationName,
  };
}

function importChartQueryDraft(
  queryDefinition: ChartQueryDefinition,
  input: {
    sourceId?: string;
    sourceName?: string;
    previewRows?: unknown[];
  } = {},
): ManualSingleSourceQueryDraft {
  return {
    kind: 'single-source',
    source: importChartQuerySource(queryDefinition, {
      sourceId: input.sourceId,
      sourceName: input.sourceName,
    }),
    measures: importMeasures(queryDefinition.measures),
    timeRange: importTimeRange(queryDefinition.timeRange),
    groupBy: importGroupBy(queryDefinition.groupBy),
    filters: queryDefinition.filters,
    orderBy: importOrderBy(queryDefinition.orderBy),
    limit: queryDefinition.limit,
    previewRows: importPreviewRows(input.previewRows),
  };
}

function buildTemplateQuery(queryDefinition: ChartQueryDefinition): TemplateQuery {
  return {
    schemaId: queryDefinition.schemaId,
    groupId: queryDefinition.groupId,
    measures: queryDefinition.measures.map((measure) => ({
      field: measure.field,
      aggregation: measure.aggregation,
      ...(measure.alias ? { alias: measure.alias } : {}),
    })),
    ...(queryDefinition.groupBy ? { groupBy: queryDefinition.groupBy } : {}),
    ...(queryDefinition.filters ? { filters: queryDefinition.filters } : {}),
    ...(queryDefinition.timeRange ? { timeRange: queryDefinition.timeRange } : {}),
    ...(queryDefinition.orderBy ? { orderBy: queryDefinition.orderBy } : {}),
    ...(typeof queryDefinition.limit === 'number' ? { limit: queryDefinition.limit } : {}),
  };
}

function buildManualSingleSourceResult(
  draft: ManualSingleSourceQueryDraft,
): Extract<CreateTemplateResult, { kind: 'template' }> | null {
  if (!draft.source) {
    return null;
  }

  const queryDefinition = buildChartQueryDefinition(draft.source, draft.measures, {
    timeRange: draft.timeRange,
    groupBy: draft.groupBy,
    filters: draft.filters,
    orderBy: draft.orderBy,
    limit: draft.limit,
  });

  if (!queryDefinition) {
    return null;
  }

  const metadata: CreateResultMetadata = {
    resultTypeLabel: 'Single Source',
    shapeLabel: 'Single Source',
    intentCategory: 'single_metric_over_time',
    requestedIntentCategory: 'single_metric_over_time',
    needsReview: false,
    needsClarification: false,
  };

  return {
    kind: 'template',
    response: {
      success: true,
      answer: 'Manual template draft is ready.',
      data: draft.previewRows ?? [],
      source: {
        id: queryDefinition.schemaId,
        name: queryDefinition.schemaName,
      },
      templateQuery: buildTemplateQuery(queryDefinition),
    },
    metadata,
  };
}

function buildCompositeSource(source: ManualCompositeSourceDraft): CompositeQueryDefinition['sources'][number] | null {
  if (!source.source) {
    return null;
  }

  const id = normalizeOptionalString(source.id);
  const query = buildChartQueryDefinition(source.source, source.measures, {
    timeRange: source.timeRange,
    groupBy: source.groupBy,
    filters: source.filters,
    orderBy: source.orderBy,
    limit: source.limit,
  });

  if (!id || !query) {
    return null;
  }

  return {
    id,
    label: normalizeOptionalString(source.label) ?? query.schemaName,
    query,
  };
}

function buildJoinKeys(joinKeys: ManualJoinKeyDraft[]): CompositeJoinKey[] {
  const normalized: CompositeJoinKey[] = [];

  for (const joinKey of joinKeys) {
    const left = normalizeOptionalString(joinKey.left);
    const right = normalizeOptionalString(joinKey.right);
    if (!left || !right) {
      continue;
    }

    if (joinKey.kind === 'nearest-timestamp') {
      if (typeof joinKey.toleranceMs === 'number') {
        normalized.push({
            left,
            right,
            kind: 'nearest-timestamp',
            toleranceMs: joinKey.toleranceMs,
            ...(isTimeGranularity(joinKey.granularity) ? { granularity: joinKey.granularity } : {}),
          });
      }
      continue;
    }

    if (joinKey.kind === 'time-bucket') {
      if (isTimeGranularity(joinKey.granularity)) {
        normalized.push({ left, right, kind: 'time-bucket', granularity: joinKey.granularity });
      }
      continue;
    }

    normalized.push({ left, right, kind: 'exact' });
  }

  return normalized;
}

function buildFieldMappings(fields: ManualCompositeFieldDraft[]): CompositeFieldMapping[] {
  return fields.flatMap((field) => {
    const sourceId = normalizeOptionalString(field.sourceId);
    const fieldName = normalizeOptionalString(field.field);
    const alias = normalizeOptionalString(field.alias);
    if (!sourceId || !fieldName || !alias) {
      return [];
    }

    return [{ sourceId, field: fieldName, alias }];
  });
}

function importCompositeJoinKey(joinKey: CompositeJoinKey): ManualJoinKeyDraft {
  if (joinKey.kind === 'nearest-timestamp') {
    return {
      left: joinKey.left,
      right: joinKey.right,
      kind: 'nearest-timestamp',
      toleranceMs: joinKey.toleranceMs,
      ...(joinKey.granularity ? { granularity: joinKey.granularity } : {}),
    };
  }

  if (joinKey.kind === 'time-bucket') {
    return {
      left: joinKey.left,
      right: joinKey.right,
      kind: 'time-bucket',
      ...(joinKey.granularity ? { granularity: joinKey.granularity } : {}),
    };
  }

  return {
    left: joinKey.left,
    right: joinKey.right,
    kind: 'exact',
    ...(joinKey.granularity ? { granularity: joinKey.granularity } : {}),
  };
}

function importCompositeSource(
  source: CompositeQueryDefinition['sources'][number],
): ManualCompositeSourceDraft {
  return {
    id: source.id,
    label: source.label,
    source: importChartQuerySource(source.query),
    measures: importMeasures(source.query.measures),
    timeRange: importTimeRange(source.query.timeRange),
    groupBy: importGroupBy(source.query.groupBy),
    filters: source.query.filters,
    orderBy: importOrderBy(source.query.orderBy),
    limit: source.query.limit,
  };
}

function generatedResultIsPeriodComparison(result: Extract<CreateTemplateResult, { kind: 'composite' }>): boolean {
  return result.metadata.resultTypeLabel === 'Period Comparison' || result.metadata.shapeLabel === 'Period Comparison';
}

function importCompositeDraft(
  result: Extract<CreateTemplateResult, { kind: 'composite' }>,
): ManualCompositeQueryDraft | ManualPeriodComparisonDraft {
  const baseDraft = {
    sources: result.definition.sources.map(importCompositeSource),
    primarySourceId: result.definition.primarySourceId,
    joinType: result.definition.joinType,
    joinKeys: result.definition.joinKeys.map(importCompositeJoinKey),
    fields: result.definition.fields.map((field) => ({
      sourceId: field.sourceId,
      field: field.field,
      alias: field.alias,
    })),
    computedFields: result.definition.computedFields?.map((field) => ({
      name: field.name,
      expression: field.expression,
    })),
    orderBy: importOrderBy(result.definition.orderBy),
    limit: result.definition.limit,
    previewRows: importPreviewRows(result.previewRows),
  };

  return generatedResultIsPeriodComparison(result)
    ? { kind: 'period-comparison', ...baseDraft }
    : { kind: 'composite', ...baseDraft };
}

function getGeneratedManualImportDisabledReason(
  result: CreateTemplateResult,
  options: GeneratedCreateActionOptions,
): string | null {
  const disabledReason = getGeneratedCreateDisabledReasons(result, options).save;
  if (disabledReason) {
    return disabledReason;
  }

  if (result.kind === 'clarification') {
    return result.metadata.saveDisabledReason ?? result.metadata.clarificationReason ?? GENERATED_IMPORT_FALLBACK_REASON;
  }

  if (result.kind === 'template' && !result.response.templateQuery) {
    return result.metadata.saveDisabledReason ?? result.metadata.reviewReason ?? GENERATED_IMPORT_FALLBACK_REASON;
  }

  return null;
}

export function buildManualDraftFromGeneratedCreateResult(
  result: CreateTemplateResult,
  options: GeneratedCreateActionOptions = {},
): GeneratedManualDraftImportResult {
  const disabledReason = getGeneratedManualImportDisabledReason(result, options);
  if (disabledReason) {
    return { ok: false, disabledReason };
  }

  if (result.kind === 'clarification') {
    return { ok: false, disabledReason: GENERATED_IMPORT_FALLBACK_REASON };
  }

  if (result.kind === 'composite') {
    const draft = importCompositeDraft(result);
    const validation = validateManualCreateDraft(draft);
    if (validation.ok) {
      return { ok: true, draft };
    }

    return validation.disabledReason === MISSING_JOIN_KEY_REASON ||
      validation.disabledReason === INVALID_JOIN_SOURCE_REASON
      ? { ok: true, draft, warning: INCOMPLETE_COMPOSITE_JOIN_IMPORT_WARNING }
      : validation;
  }

  const organizationScope = resolveGeneratedCreateSingleSourceOrganizationScope(result, options);
  const queryResolution = buildGeneratedCreateSingleSourceQueryDefinition({
    templateQuery: result.response.templateQuery,
    templateVariables: result.response.templateVariables,
    schemaName: result.response.source?.name,
    organizationId: organizationScope?.organizationId,
    organizationName: organizationScope?.organizationName,
  });

  if (!queryResolution.ok) {
    return queryResolution;
  }

  const draft = importChartQueryDraft(queryResolution.queryDefinition, {
    sourceId: result.response.source?.id,
    sourceName: result.response.source?.name,
    previewRows: result.response.data,
  });
  const validation = validateManualCreateDraft(draft);
  return validation.ok ? { ok: true, draft } : validation;
}

export function updateManualSingleSourceDraftClearingPreview(
  draft: ManualSingleSourceQueryDraft,
  update: (currentDraft: ManualSingleSourceQueryDraft) => ManualSingleSourceQueryDraft,
): ManualSingleSourceQueryDraft {
  const updatedDraft = update(draft);
  return updatedDraft.previewRows === undefined
    ? updatedDraft
    : {
        ...updatedDraft,
        previewRows: undefined,
      };
}

function buildComputedFields(fields: ManualComputedFieldDraft[] | undefined): ComputedField[] | undefined {
  const normalized = (fields ?? []).flatMap((field) => {
    const name = normalizeOptionalString(field.name);
    const expression = normalizeOptionalString(field.expression);
    return name && expression ? [{ name, expression }] : [];
  });

  return normalized.length > 0 ? normalized : undefined;
}

function buildManualCompositeDefinition(
  draft: ManualCompositeQueryDraft | ManualPeriodComparisonDraft,
): CompositeQueryDefinition | null {
  const primarySourceId = normalizeOptionalString(draft.primarySourceId);
  const computedFields = buildComputedFields(draft.computedFields);
  const orderBy = buildOrderBy(draft.orderBy);
  if (!primarySourceId) {
    return null;
  }

  return {
    sources: draft.sources.flatMap((source) => {
      const compositeSource = buildCompositeSource(source);
      return compositeSource ? [compositeSource] : [];
    }),
    primarySourceId,
    joinType: draft.joinType,
    joinKeys: buildJoinKeys(draft.joinKeys),
    fields: buildFieldMappings(draft.fields),
    ...(computedFields ? { computedFields } : {}),
    ...(orderBy ? { orderBy } : {}),
    ...(typeof draft.limit === 'number' ? { limit: draft.limit } : {}),
  };
}

function buildManualCompositeResult(
  draft: ManualCompositeQueryDraft | ManualPeriodComparisonDraft,
): Extract<CreateTemplateResult, { kind: 'composite' }> | null {
  const definition = buildManualCompositeDefinition(draft);
  if (!definition) {
    return null;
  }

  const isPeriodComparison = draft.kind === 'period-comparison';
  const metadata: CreateResultMetadata = {
    resultTypeLabel: isPeriodComparison ? 'Period Comparison' : 'Combined Sources',
    shapeLabel: isPeriodComparison ? 'Period Comparison' : 'Combined Sources',
    intentCategory: isPeriodComparison ? 'period_comparison' : 'multi_source_combined',
    requestedIntentCategory: isPeriodComparison ? 'period_comparison' : 'multi_source_combined',
    needsReview: false,
    needsClarification: false,
  };

  return {
    kind: 'composite',
    explanation: isPeriodComparison
      ? 'Manual period-comparison draft is ready.'
      : 'Manual composite template draft is ready.',
    definition,
    previewRows: draft.previewRows ?? [],
    metadata,
  };
}

export function buildManualCreateTemplateResult(draft: ManualCreateDraft): ManualCreateTemplateResult {
  const validation = validateManualCreateDraft(draft);
  if (!validation.ok) {
    return validation;
  }

  const result =
    draft.kind === 'single-source'
      ? buildManualSingleSourceResult(draft)
      : buildManualCompositeResult(draft);

  return result
    ? { ok: true, result }
    : { ok: false, disabledReason: 'Manual draft could not be converted into a reusable template.' };
}

export function getManualCreateActionState(draft: ManualCreateDraft): ManualCreateActionState {
  const result = buildManualCreateTemplateResult(draft);
  if (!result.ok) {
    return {
      isActionable: false,
      canSave: false,
      canApply: false,
      save: result.disabledReason,
      apply: result.disabledReason,
    };
  }

  return {
    isActionable: true,
    canSave: true,
    canApply: true,
    save: null,
    apply: null,
  };
}
